import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider } from "@mui/material/styles";
import { styled } from "@mui/system";
import { APP_CONFIG } from "config/appConfig";
import { CspContextProvider } from "context/CspContext";
import { GlobalModalProvider } from "context/GlobalModalContext";
import { AppContext } from "context/appContext";
import SubscriptionProvider from "context/subscriptionContext";
import { useAuth } from "hooks/useAuth";
import About from "pages/About";
import Dashboard from "pages/Dashboard";
import ForgotPassword from "pages/ForgotPassword";
import Home from "pages/Home";
import NotFound from "pages/NotFound";
import SignUp from "pages/SignUp";
import EventDetails from "pages/events/EventDetails";
import Events from "pages/events/Events";
import ActionDetails from "pages/events/ActionDetails";
import AddEventVariable from "pages/events/AddEventVariable";
import ConditionDetails from "pages/events/ConditionDetails";
import EventVariableDetail from "pages/events/EventVariableDetail";
import ActuatorDetails from "pages/gateways/ActuatorDetails";
import AddGateWay from "pages/gateways/AddGateway";
import AddLoRaWANNode from "pages/gateways/AddLoRaWANNode";
import AddNewGroup from "pages/gateways/AddNewGroup";
import GatewayDetails from "pages/gateways/GatewayDetails";
import GatewayGroupDetails from "pages/gateways/GatewayGroupDetails";
import {
    AllGateway,
    GatewayGroups,
    Standalone,
} from "pages/gateways/GatewayList";
import GatewayLogPage from "pages/gateways/GatewayLogPage";
import LDSBus from "pages/gateways/LDSBus";
import LdsuDetails from "pages/gateways/LdsuDetails";
import SensorDetails from "pages/gateways/SensorDetails";
import Uart from "pages/gateways/Uart";
import AddNetwork from "pages/networks/AddNetwork";
import AuxiliaryHubDetails from "pages/networks/AuxiliaryHubDetails";
import NetworkControllerDetails from "pages/networks/ControllerDetails";
import GroupDetails from "pages/networks/GroupDetails";
import HubDetails from "pages/networks/HubDetails";
import NetworkDetails from "pages/networks/NetworkDetails";
import Networks from "pages/networks/NetworksList";
import RoomDetails from "pages/networks/RoomDetails";
import NetworkSensorDetails from "pages/networks/SensorDetails";
import ZoneDetails from "pages/networks/ZoneDetails";
import EditOrganization from "pages/organizations/EditOrganization";
import GroupsDetails from "pages/organizations/GroupsDetails";
import InviteUsers from "pages/organizations/InviteMembers";
import MembersDetails from "pages/organizations/MembersDetails";
import NewGroup from "pages/organizations/NewGroup";
import NewRole from "pages/organizations/NewRole";
import OrganizationDetails from "pages/organizations/OrganizationDetails";
import RolesDetails from "pages/organizations/RolesDetails";
import AccountSettings from "pages/profiles/AccountSettings";
import ChangePassword from "pages/profiles/ChangePassword";
import DownloadData from "pages/profiles/DownloadData";
import UserProfile from "pages/profiles/UserProfile";
import Invitations from "pages/profiles/invitations/Invitations";
import TerminateAccount, {
    TerminateSuccess,
} from "pages/profiles/terminate-account";
import Reports from "pages/reports/Reports";
import ManageAccessKeys from "pages/settings/ManageAccessKeys";
import Settings from "pages/settings/Settings";
import TimezoneSettings from "pages/settings/TimezoneSettings";
import Checkout from "pages/subscriptions/Checkout";
import CheckoutBillingAddress from "pages/subscriptions/CheckoutBillingAddress";
import ManageSubscription from "pages/subscriptions/ManageSubscription";
import Subscriptions from "pages/subscriptions/Subscriptions";
import Spinner from "components/common/Spinner";
import React, { useEffect, useMemo, useReducer } from "react";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import ScrollToTop from "components/common/ScrollToTop";
import "react-toastify/dist/ReactToastify.css";
import AppReducer from "store/reducer";
import DefaultAppState from "store/state";
import { useAuthStore } from "store/useAuthStore";
import styledConst from "styles";
import "utils/authorize-action";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import muiTheme from "./styles/muiTheme";
import DashboardCreate from "./components/dashboard/create";
import DashboardPanelUpdate from "./components/dashboard/update/single";

// Function to render event routes conditionally
const renderOnPremRoutes = () => {
    let routes = [];

    if (!APP_CONFIG.APP_IS_ONPREM) {
        routes.push(
            <PublicRoute
                key="forgot-password"
                path="/forgot-password"
                component={ForgotPassword}
            />
        );
    }
    if (APP_CONFIG.APP_IS_ONPREM) {
        routes.push(
            <PrivateRoute
                key="networks"
                path="/networks"
                component={Networks}
            />,
            <PrivateRoute
                key="networks-add"
                path="/networks-add"
                component={AddNetwork}
            />,
            <PrivateRoute
                exact
                key="network-details"
                path="/network-details/:id"
                component={NetworkDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-zone-details-room-details-sensor-details"
                path="/network-details/:id/zone/:zoneId/room-details/:roomId/sensor-details/:sensorId"
                component={NetworkSensorDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-zone-details-sensor-details"
                path="/network-details/:id/zone/:zoneId/sensor-details/:sensorId"
                component={NetworkSensorDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-zone-details-room-details-controller-details"
                path="/network-details/:id/zone/:zoneId/room-details/:roomId/controller-details/:controllerId"
                component={NetworkControllerDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-zone-details-controller-details"
                path="/network-details/:id/zone/:zoneId/controller-details/:controllerId"
                component={NetworkControllerDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-zone-details-room-details"
                path="/network-details/:id/zone/:zoneId/room-details/:roomId"
                component={RoomDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-room-details"
                path="/network-details/:id/room-details/:roomId"
                component={RoomDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-room-details-sensor-details"
                path="/network-details/:id/room-details/:roomId/sensor-details/:sensorId"
                component={NetworkSensorDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-room-details-controller-details"
                path="/network-details/:id/room-details/:roomId/controller-details/:controllerId"
                component={NetworkControllerDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-group-details"
                path="/network-details/:id/group-details/:groupId"
                component={GroupDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-group-details-sensor-details"
                path="/network-details/:id/group-details/:groupId/sensor-details/:sensorId"
                component={NetworkSensorDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-group-details-controller-details"
                path="/network-details/:id/group-details/:groupId/controller-details/:controllerId"
                component={NetworkControllerDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-zone-details"
                path="/network-details/:id/zone/:zoneId"
                component={ZoneDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-hub-details"
                path="/network-details/:id/hub-details/:hubId"
                component={HubDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-hub-details-sensor-details"
                path="/network-details/:id/hub-details/:hubId/sensor-details/:sensorId"
                component={NetworkSensorDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-hub-details-controller-details"
                path="/network-details/:id/hub-details/:hubId/controller-details/:controllerId"
                component={NetworkControllerDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-sensor-details"
                path="/network-details/:id/sensor-details/:sensorId"
                component={NetworkSensorDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-controller-details"
                path="/network-details/:id/controller-details/:controllerId"
                component={NetworkControllerDetails}
            />,
            <PrivateRoute
                exact
                key="network-details-auxiliary-hub-details"
                path="/network-details/:id/auxiliary-hub-details/:auxiliaryHubId"
                component={AuxiliaryHubDetails}
            />,

            <PrivateRoute key="reports" path="/reports" component={Reports} />
        );
    } else {
        routes.push(
            <PrivateRoute key="events" path="/events" component={Events} />,
            <PrivateRoute
                key="event-variables-add"
                path="/event-variables-add"
                component={AddEventVariable}
            />,
            <PrivateRoute
                key="event-variables-detail"
                path="/event-variables/:eventVariableId"
                component={EventVariableDetail}
            />,
            <PrivateRoute
                key="event-details"
                exact
                path="/event-details/:eventId"
                component={EventDetails}
            />,
            <PrivateRoute
                key="event-condition-details"
                path="/event-details/:eventId/condition/:condition/:conditionId?"
                component={ConditionDetails}
            />,
            <PrivateRoute
                key="event-action-details"
                path="/event-details/:eventId/action/:action/:actionId?"
                component={ActionDetails}
            />
        );
    }

    return routes.length > 0 ? routes : null;
};

const StyledToastContainer = styled(ToastContainer)`
    .Toastify__toast-body {
        .description {
            color: ${styledConst.Primary_Blue_4};
            font-size: 11px;
            b {
                color: ${styledConst.Primary_Blue_3};
            }
            word-break: break-word;
        }
        align-items: flex-start;
    }

    .Toastify__toast-icon {
        padding-top: 2px;
    }

    .Toastify__toast--error {
        background: ${styledConst.errorBackground};
        .Toastify__toast-body {
            align-items: flex-start;

            .content {
                margin-top: 0px;
            }

            .description {
                margin-top: 0.6rem;
            }

            .title,
            svg {
                color: ${styledConst.Secondary_Red};
                font-size: 14px;
            }
        }
        .Toastify__close-button {
            color: ${styledConst.Secondary_Red};
        }
    }

    .Toastify__toast--success {
        background: ${styledConst.successBackground};
        .Toastify__toast-body {
            .title,
            svg {
                color: ${styledConst.Primary_Green_1};
                font-size: 14px;
            }
        }
        .Toastify__close-button {
            color: ${styledConst.Primary_Green_1};
        }
    }

    .Toastify__toast--info {
        background: ${styledConst.infoBackground};
        .Toastify__toast-body {
            .title,
            svg {
                color: ${styledConst.Primary_Blue};
                font-size: 14px;
            }
        }
        .Toastify__close-button {
            color: ${styledConst.Primary_Blue};
        }
    }
`;

const App = ({ nonce }: Readonly<{ nonce: string }>) => {
    const [state, dispatch] = useReducer(AppReducer, DefaultAppState);
    const store = React.useMemo(() => [state, dispatch], [state]);

    // Get auth functions and state
    const initializeAuth = useAuth().initializeAuth;

    // Initialize auth ONLY ONCE at app level
    useEffect(() => {
        initializeAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get initialization state from store
    const isInitializing = useAuthStore((state) => state.isInitializing);
    const setInitializing = useAuthStore((state) => state.setInitializing);

    useEffect(() => {
        const handleStoreChange = (event: any) => {
            // Only reload when lastLoggedOrgId is SET (has a value)
            // This happens on logout to preserve org selection
            // Don't reload when it's removed during normal org setup
            if (event.key === "lastLoggedOrgId" && event.newValue) {
                globalThis.location.reload();
            }
        };
        globalThis.addEventListener("storage", handleStoreChange, true);
        return () => {
            globalThis.removeEventListener("storage", handleStoreChange);
        };
    }, []); // Add empty dependency array to prevent re-creating listener

    useEffect(() => {
        if (!isInitializing) {
            return;
        }

        // Global safety net: never block app rendering forever on auth init.
        const timeoutId = globalThis.window.setTimeout(() => {
            setInitializing(false);
        }, 10000);

        return () => {
            globalThis.window.clearTimeout(timeoutId);
        };
    }, [isInitializing, setInitializing]);

    const memoizedStoreObject = useMemo(
        () => ({ storeData: store[0], storeDispatchActions: store[1] }),
        [store]
    );

    const emotionCache = useMemo(
        () =>
            createCache({
                key: "wmc",
                nonce,
                prepend: false, // prepend: false will help prioritise emotion styles over that of other libraries
            }),
        [nonce]
    );

    // Early return for spinner while auth initializes
    if (isInitializing) {
        return <Spinner />;
    }

    return (
        <AppContext.Provider value={memoizedStoreObject}>
            <SubscriptionProvider>
                <CspContextProvider nonce={nonce}>
                    <CacheProvider value={emotionCache}>
                        <ThemeProvider theme={muiTheme}>
                            <GlobalModalProvider>
                                <StyledToastContainer />
                                <Router basename={APP_CONFIG.APP_PUBLIC_URL || "/"}>
                                    <ScrollToTop />
                                    <Switch>
                                        <PublicRoute
                                            path="/sign-up"
                                            component={SignUp}
                                        />
                                        <PublicRoute
                                            exact
                                            path="/"
                                            component={Home}
                                        />

                                        <PrivateRoute
                                            exact
                                            path="/dashboard"
                                            component={Dashboard}
                                        />
                                        <PrivateRoute
                                            exact
                                            path="/dashboard/:dashboardId/new-charts"
                                            component={DashboardCreate}
                                        />

                                        <PrivateRoute
                                            exact
                                            path="/dashboard/:dashboardId/update/:panelId"
                                            component={DashboardPanelUpdate}
                                        />
                                        <PrivateRoute
                                            path="/gateways-all"
                                            component={AllGateway}
                                        />
                                        <PrivateRoute
                                            path="/gateways"
                                            component={Standalone}
                                        />
                                        <PrivateRoute
                                            path="/gateways-add"
                                            component={AddGateWay}
                                        />
                                        <PrivateRoute
                                            path="/lorawan-add"
                                            component={AddLoRaWANNode}
                                        />
                                        <PrivateRoute
                                            path="/gateways-group"
                                            component={GatewayGroups}
                                        />
                                        <PrivateRoute
                                            path="/new-group"
                                            component={AddNewGroup}
                                        />
                                        <PrivateRoute
                                            exact
                                            path="/gateway-details/:gatewayId"
                                            component={GatewayDetails}
                                        />
                                        <PrivateRoute
                                            path="/gateway-details/:gatewayId/self-check-error-log"
                                            component={GatewayLogPage}
                                        />
                                        <PrivateRoute
                                            path="/gate-group-details/:groupId"
                                            component={GatewayGroupDetails}
                                        />
                                        <PrivateRoute
                                            exact
                                            path="/gateway-details/:gatewayId/LDSBus/:busNumber"
                                            component={LDSBus}
                                        />
                                        <PrivateRoute
                                            path="/gateway-details/:gatewayId/m2m"
                                            component={Uart}
                                        />
                                        <PrivateRoute
                                            path="/gateway-details/:gatewayId/LDSBus/:busNumber/:ldsuId"
                                            component={LdsuDetails}
                                        />
                                        <PrivateRoute
                                            path="/sensor-details/:gatewayId/LDSBus/:busNumber/:ldsuId/:said"
                                            component={SensorDetails}
                                        />
                                        <PrivateRoute
                                            path="/actuator-details/:gatewayId/:busNumber/:ldsuId/:said"
                                            component={ActuatorDetails}
                                        />

                                        {renderOnPremRoutes()}

                                        <PrivateRoute
                                            path="/create-organisation"
                                            component={EditOrganization}
                                        />
                                        <PrivateRoute
                                            path="/edit-organisation/:orgId"
                                            component={EditOrganization}
                                        />
                                        <PrivateRoute
                                            path="/invite-users/:orgId"
                                            component={InviteUsers}
                                        />
                                        <PrivateRoute
                                            path="/new-org-group/:orgId"
                                            component={NewGroup}
                                        />
                                        <PrivateRoute
                                            path="/new-org-role/:orgId"
                                            component={NewRole}
                                        />
                                        <PrivateRoute
                                            path="/organisation/:orgId/member/:memberId"
                                            component={MembersDetails}
                                        />
                                        <PrivateRoute
                                            path="/organisation/:orgId/group/:groupId"
                                            component={GroupsDetails}
                                        />
                                        <PrivateRoute
                                            path="/organisation/:orgId/role/:roleId"
                                            component={RolesDetails}
                                        />
                                        <PrivateRoute
                                            path="/organisation/:orgId"
                                            component={OrganizationDetails}
                                        />

                                        <PrivateRoute
                                            exact
                                            path="/settings"
                                            component={Settings}
                                        />
                                        <PrivateRoute
                                            path="/settings/manage-api-access-keys"
                                            component={ManageAccessKeys}
                                        />
                                        <PrivateRoute
                                            path="/settings/timezone"
                                            component={TimezoneSettings}
                                        />
                                        <PrivateRoute
                                            path="/about"
                                            component={About}
                                        />

                                        <PrivateRoute
                                            path="/manage-subscription"
                                            component={Subscriptions}
                                        />
                                        <PrivateRoute
                                            path="/manage-subscription-details"
                                            component={ManageSubscription}
                                        />

                                        <PrivateRoute
                                            exact
                                            path="/checkout"
                                            component={Checkout}
                                        />
                                        <PrivateRoute
                                            path="/checkout/billing-address"
                                            component={CheckoutBillingAddress}
                                        />

                                        <PrivateRoute
                                            path="/user/settings"
                                            component={AccountSettings}
                                        />
                                        <PrivateRoute
                                            path="/user/profile"
                                            component={UserProfile}
                                        />
                                        <PrivateRoute
                                            path="/user/change-password"
                                            component={ChangePassword}
                                        />
                                        <PrivateRoute
                                            path="/user/invitations"
                                            component={Invitations}
                                        />
                                        <PrivateRoute
                                            path="/user/terminate"
                                            component={TerminateAccount}
                                        />
                                        <Route
                                            path="/user/terminate-success"
                                            component={TerminateSuccess}
                                        />
                                        <PrivateRoute
                                            path="/user/download-data"
                                            component={DownloadData}
                                        />
                                        <Route path="/callback" component={Home} />
                                        <Route component={NotFound} />
                                    </Switch>
                                </Router>
                            </GlobalModalProvider>
                        </ThemeProvider>
                    </CacheProvider>
                </CspContextProvider>
            </SubscriptionProvider>
        </AppContext.Provider>
    );
};

export default App;
